import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Avatar, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { darken } from '@mui/material/styles';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Calendar, Event, EventProps, momentLocalizer, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Fulfillment } from 'api/models/Fulfillment';
import { ApiService } from 'api/services/ApiService';
import { useZoom } from 'context/ZoomContext';
import { getCalendarColorPalette, getEngagementTypeColor, getStatusColor } from 'dashboard409a/hooks/useColorPalette';
import { getInitials } from 'utilities/getInitials';
import { CalendarEvent, ModelsCalendarProps } from './ModelsCalendar.types';
import {
  CALENDAR_DATE_FORMAT,
  CALENDAR_DATE_KEY,
  CALENDAR_EVENT_BASE_STYLES,
  CALENDAR_MESSAGES,
  CALENDAR_STYLES,
  CALENDAR_VIEW_KEY,
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_VIEW,
  MODELS_CALENDAR_EVENT_TEXT_COLORS,
  MODELS_CALENDAR_STATUS_TEXT_COLOR_MAP,
} from '../../common/constants/calendar';
import { reverseModelStatusMap } from '../../pages/Operations/components/ModelStatus/ModelStatus.constants';
import { ModelWithValuationData } from '../../services/models.types';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import './styles.scss';

const localizer = momentLocalizer(moment);
// Type assertion needed because withDragAndDrop doesn't preserve generic types
// We know our events are CalendarEvent, so we can safely assert the types
// Using Record<string, unknown> to allow additional drag-and-drop props
const DragAndDropCalendar = withDragAndDrop(Calendar) as React.ComponentType<
  React.ComponentProps<typeof Calendar> & {
    onEventDrop?: (args: { event: CalendarEvent; start: Date; end: Date }) => void;
    draggableAccessor?: (event: Event) => boolean;
  } & Record<string, unknown>
>;

const EventComponent = (props: EventProps) => {
  const { event } = props;
  const calendarEvent = event as CalendarEvent;
  const model = calendarEvent.resource;
  const companyName = model.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY;
  const valuationDate = model.valuation_date ? moment(model.valuation_date).format('MMM, DD, YY') : '';
  const statusColor = getStatusColor(model.model_status ?? 0);
  const statusLabel = reverseModelStatusMap[model.model_status as keyof typeof reverseModelStatusMap] || '';

  const hasNotes = typeof model.notes_count === 'number' && model.notes_count > 0;
  const latestNote = model.note;
  const latestNoteAuthor = latestNote?.author_name || latestNote?.author_email || '';
  const latestNoteCreatedAt = latestNote?.created_at ? moment(latestNote.created_at).format('MMM D, YYYY h:mm A') : '';
  const latestNoteContent = (latestNote?.content ?? '').trim();
  const latestNotePreview = latestNoteContent.length > 140
    ? `${latestNoteContent.slice(0, 140)}…`
    : latestNoteContent;

  // Backend types for Fulfillment mark preparers/reviewers as `number[]`,
  // but the calendar payload is enriched with profile objects.
  const preparers = (model.preparers ?? []) as unknown as Array<{
    id: number;
    picture_url?: string | null;
    first_name: string;
    last_name: string;
  }>;

  const reviewers = (model.reviewers ?? []) as unknown as Array<{
    id: number;
    picture_url?: string | null;
    first_name: string;
    last_name: string;
  }>;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', padding: 0.5, margin: '4px 0'}}>
      <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ backgroundColor: statusColor, padding: 0.5, borderRadius: 1 }}>
        <Typography variant="caption" fontSize="0.6rem" fontWeight="bold" sx={{ color: '#333333' }}>
          {statusLabel}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ 
            lineHeight: 1.2,
            color: '#333333',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {companyName}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" sx={{ padding: 0.5, borderRadius: 1, flexWrap: 'wrap', gap: 0.5 }}>
        {model.engagement_type && (
          <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ color: 'white', backgroundColor: getEngagementTypeColor(model.engagement_type), padding: 0.5, borderRadius: 1 }}>
            <Typography variant="caption" fontSize="0.7rem" sx={{color: 'white'}}>
              {model.engagement_type}
            </Typography>
          </Box>
        )}
        {valuationDate && (
          <Box display="flex" flexDirection="column" justifyContent="space-between" sx={{ backgroundColor: getCalendarColorPalette().LABELS, padding: 0.5, borderRadius: 1 }}>
            <Typography variant="caption" fontSize="0.7rem" sx={{color: 'white'}}>
              {valuationDate}
            </Typography>
          </Box>
        )}
        {hasNotes && (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ maxWidth: 320, backgroundColor: '#333333', padding: 1, borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: 'white', display: 'block' }}>
                  {latestNoteAuthor || 'Analyst'} {latestNoteCreatedAt ? `• ${latestNoteCreatedAt}` : ''}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'white', whiteSpace: 'pre-wrap', mt: 0.5 }}
                >
                  {latestNotePreview || '—'}
                </Typography>
              </Box>
            }
          >
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ backgroundColor: getCalendarColorPalette().LABELS, padding: 0.5, borderRadius: 1 }}
            >
              <ChatBubbleOutlineIcon sx={{ color: 'white', fontSize: '1rem' }} />
              <Typography variant="caption" fontSize="0.7rem" sx={{ color: 'white' }}>
                {model.notes_count}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Box display="flex" gap={0.5}>
          {preparers.map(preparer => (
            <Avatar
              key={preparer.id}
              src={preparer.picture_url ?? undefined}
              sx={{ width: 32, height: 32, fontSize: '1rem', fontWeight: 'bolder' }}
              style={{ backgroundColor: darken(getCalendarColorPalette().LABELS, 0.2) }}
            >
              {getInitials({ name: `${preparer.first_name || ''} ${preparer.last_name || ''}` })}
            </Avatar>
          ))}
        </Box>
        <Box display="flex" gap={0.5}>
          {reviewers.map(reviewer => (
            <Avatar
              key={reviewer.id}
              src={reviewer.picture_url ?? undefined}
              sx={{ width: 32, height: 32, fontSize: '1rem', fontWeight: 'bolder' }}
              style={{ backgroundColor: darken(getCalendarColorPalette().LABELS, 0.2) }}
            >
              {getInitials({ name: `${reviewer.first_name || ''} ${reviewer.last_name || ''}` })}
            </Avatar>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const ModelsCalendar = ({
  models,
  isLoading,
  onSelectEvent,
  onOpenPreview,
  onEventDrop,
  hideToolbar = false,
  controlledDate,
  controlledView,
  onDateChange,
  onViewChange: onViewChangeProp,
}: ModelsCalendarProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { zoomLevel } = useZoom();
  const [updatingEvents, setUpdatingEvents] = useState<Set<number>>(new Set());
  const calendarRef = useRef<HTMLDivElement>(null);
  // Restore calendar date and view from localStorage
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    if (globalThis.window) {
      const savedDate = globalThis.window.localStorage.getItem(CALENDAR_DATE_KEY);
      if (savedDate) {
        const date = moment(savedDate, CALENDAR_DATE_FORMAT);
        if (date.isValid()) {
          return date.toDate();
        }
      }
    }
    return new Date();
  });

  const [currentView, setCurrentView] = useState<View>(() => {
    if (globalThis.window) {
      const savedView = globalThis.window.localStorage.getItem(CALENDAR_VIEW_KEY) as View;
      if (savedView && CALENDAR_VIEWS.includes(savedView as typeof CALENDAR_VIEWS[number])) {
        return savedView;
      }
    }
    return DEFAULT_CALENDAR_VIEW;
  });

  // Save date to localStorage when it changes
  useEffect(() => {
    if (globalThis.window) {
      globalThis.window.localStorage.setItem(CALENDAR_DATE_KEY, moment(currentDate).format(CALENDAR_DATE_FORMAT));
    }
  }, [currentDate]);

  // Save view to localStorage only when it's not the default (calendar)
  // If calendar is the default, we only persist when they switch to table view
  useEffect(() => {
    if (globalThis.window) {
      if (currentView === DEFAULT_CALENDAR_VIEW) {
        // Remove from localStorage when returning to default view
        globalThis.window.localStorage.removeItem(CALENDAR_VIEW_KEY);
      } else {
        globalThis.window.localStorage.setItem(CALENDAR_VIEW_KEY, currentView);
      }
    }
  }, [currentView]);

  // Convert models to calendar events
  const events: CalendarEvent[] = useMemo(() => models
    .filter(model => {
      // Only include models with a valid due date and id
      // supporting_due_date comes from Fulfillment type
      const dueDate = model.supporting_due_date;
      return model.id !== undefined && dueDate && dueDate !== '-' && dueDate.trim();
    })
    .map(model => {
      const dueDate = model.supporting_due_date as string;
      const date = moment(dueDate, CALENDAR_DATE_FORMAT);
        
      // Skip invalid dates
      if (!date.isValid()) {
        return null;
      }
        
      // Create event title with company name only (no status)
      const companyName = model.company_name || CALENDAR_MESSAGES.UNKNOWN_COMPANY;
      const title = companyName;

      return {
        title,
        start: date.toDate(),
        end: date.toDate(),
        resource: model,
      } as CalendarEvent;
    })
    .filter((event): event is CalendarEvent => event !== null), [models]);

  const handleSelectEvent = (event: CalendarEvent) => {
    // Save current date and view (if not default) before opening dialog
    if (globalThis.window) {
      globalThis.window.localStorage.setItem(CALENDAR_DATE_KEY, moment(activeDate).format(CALENDAR_DATE_FORMAT));
      // Only save view if it's not the default
      if (activeView === DEFAULT_CALENDAR_VIEW) {
        // Skip saving default view
      } else {
        globalThis.window.localStorage.setItem(CALENDAR_VIEW_KEY, activeView);
      }
    }

    // Open preview dialog if handler is provided, otherwise use legacy navigation
    if (onOpenPreview) {
      onOpenPreview(event.resource);
    } else if (onSelectEvent) {
      onSelectEvent(event.resource);
    }
  };

  const handleNavigate = (newDate: Date) => {
    if (onDateChange) {
      onDateChange(newDate);
    } else {
      setCurrentDate(newDate);
    }
  };

  const handleViewChange = (newView: View) => {
    if (onViewChangeProp) {
      onViewChangeProp(newView);
    } else {
      setCurrentView(newView);
    }
  };

  // Use controlled values if provided, otherwise use internal state
  const activeDate = controlledDate ?? currentDate;
  const activeView = controlledView ?? currentView;

  // Helper function to validate model fields
  const validateModelFields = useCallback((model: ModelWithValuationData): number | null => {
    if (!model.company_id) {
      enqueueSnackbar('Company ID is required to update dates.', { variant: 'error' });
      return null;
    }

    if (!model.attio_valuation_id) {
      enqueueSnackbar('Valuation ID is required to update dates.', { variant: 'error' });
      return null;
    }

    const fulfillmentId = model.id;
    if (fulfillmentId === undefined) {
      enqueueSnackbar('Model ID is required to update dates.', { variant: 'error' });
      return null;
    }

    return fulfillmentId;
  }, [enqueueSnackbar]);

  // Helper function to validate and format dates
  const validateAndFormatDates = useCallback((originalDateString: string | null | undefined, newDate: Date): { newDateString: string; originalDateString: string } | null => {
    if (!originalDateString) {
      enqueueSnackbar('Original due date is missing.', { variant: 'error' });
      return null;
    }

    const originalDate = moment(originalDateString, CALENDAR_DATE_FORMAT);
    if (!originalDate.isValid()) {
      enqueueSnackbar('Invalid original date format.', { variant: 'error' });
      return null;
    }

    const newMomentDate = moment(newDate);
    if (!newMomentDate.isValid()) {
      enqueueSnackbar('Invalid date from drag and drop.', { variant: 'error' });
      return null;
    }
    
    const formattedNewDate = newMomentDate.format(CALENDAR_DATE_FORMAT);
    const formattedOriginalDate = originalDate.format(CALENDAR_DATE_FORMAT);

    if (formattedOriginalDate === formattedNewDate) {
      return null; // No change needed
    }

    return { newDateString: formattedNewDate, originalDateString: formattedOriginalDate };
  }, [enqueueSnackbar]);

  // Helper function to extract error message
  const getErrorMessage = useCallback((error: unknown): string => {
    if (error && typeof error === 'object' && 'response' in error) {
      const apiError = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
      if (apiError.response?.status === 404) {
        return 'Model not found or you do not have access.';
      }
      if (apiError.response?.status === 400) {
        return apiError.response?.data?.message || 'Invalid date format.';
      }
      if (apiError.message) {
        return apiError.message;
      }
    }
    if (error instanceof Error) {
      return error.message;
    }
    return 'Failed to update date. Please try again.';
  }, []);

  // Helper function to perform optimistic update
  const performOptimisticUpdate = useCallback((modelId: number, newDateString: string) => {
    if (onEventDrop) {
      onEventDrop(modelId, newDateString);
    }
    setUpdatingEvents(prev => new Set(prev).add(modelId));
  }, [onEventDrop]);

  // Helper function to update API and handle success
  const updateDueDateApi = useCallback(async (
    model: ModelWithValuationData,
    newDateString: string,
    newDate: Date
  ) => {
    const updateData: Partial<Fulfillment> = {
      supporting_due_date: newDateString,
    };

    await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
      model.company_id.toString(),
      model.attio_valuation_id,
      updateData as Fulfillment
    );

    enqueueSnackbar(`Due Date updated to ${moment(newDate).format('MMM DD, YYYY')}`, { 
      variant: 'success' 
    });
  }, [enqueueSnackbar]);

  // Helper function to handle error and revert optimistic update
  const handleUpdateError = useCallback((
    error: unknown,
    modelId: number,
    originalDateString: string
  ) => {
    if (onEventDrop) {
      onEventDrop(modelId, originalDateString);
    }

    const errorMessage = getErrorMessage(error);
    enqueueSnackbar(errorMessage, { variant: 'error' });
  }, [onEventDrop, getErrorMessage, enqueueSnackbar]);

  // Helper function to clean up updating state
  const cleanupUpdatingState = useCallback((modelId: number) => {
    setUpdatingEvents(prev => {
      const newSet = new Set(prev);
      newSet.delete(modelId);
      return newSet;
    });
  }, []);

  const handleEventDrop = useCallback(async ({ event, start }: { event: Event; start: Date; end: Date }) => {
    const calendarEvent = event as CalendarEvent;
    const model = calendarEvent.resource;
    
    const modelId = validateModelFields(model);
    if (modelId === null) {
      return;
    }

    const dateValidation = validateAndFormatDates(model.supporting_due_date, start);
    if (!dateValidation) {
      return;
    }

    const { newDateString, originalDateString } = dateValidation;

    performOptimisticUpdate(modelId, newDateString);

    try {
      await updateDueDateApi(model, newDateString, start);
    } catch (error: unknown) {
      handleUpdateError(error, modelId, originalDateString);
    } finally {
      cleanupUpdatingState(modelId);
    }
  }, [validateModelFields, validateAndFormatDates, performOptimisticUpdate, updateDueDateApi, handleUpdateError, cleanupUpdatingState]);

  const eventStyleGetter = (event: Event) => {
    // Type guard to ensure event is CalendarEvent
    const calendarEvent = event as CalendarEvent;
    const status = calendarEvent.resource.model_status;
    const modelId = calendarEvent.resource.id;
    
    // Type guard: ensure status and id exist
    const statusNumber: number = status ?? -1;
    const textColor = MODELS_CALENDAR_STATUS_TEXT_COLOR_MAP[statusNumber] || MODELS_CALENDAR_EVENT_TEXT_COLORS.DEFAULT;
    const isUpdating = modelId !== undefined && updatingEvents.has(modelId);
    const palette = getCalendarColorPalette();

    return {
      style: {
        ...CALENDAR_EVENT_BASE_STYLES,
        backgroundColor: palette.BACKGROUND,
        border: `${palette.BORDER} 1px solid`,
        opacity: isUpdating ? 0.5 : CALENDAR_STYLES.EVENT_OPACITY,
        color: textColor,
      },
    };
  };

  // Fix for drag and drop with zoom: Intercept and adjust mouse coordinates
  useEffect(() => {
    if (!calendarRef.current || zoomLevel === 1) {
      return;
    }

    const calendarElement = calendarRef.current;
    
    // Intercept mouse events during drag and drop to adjust coordinates for zoom
    const adjustCoordinates = (event: MouseEvent) => {
      // Check if we're in a drag operation
      const isDragging = document.querySelector('.rbc-addons-dnd-dragging') !== null;
      if (!isDragging) {
        return;
      }

      // Get bounding box - this is already affected by root zoom
      const rect = calendarElement.getBoundingClientRect();
      
      // Mouse coordinates are in the root zoom space
      // We need to convert them to the calendar's internal coordinate space
      // When root zoom is 0.8, everything is 0.8x smaller
      // The calendar container has inverse zoom (1/0.8 = 1.25) to cancel it out
      // So internally, the calendar thinks it's 1.25x larger
      // We need to adjust mouse coordinates to match this internal size
      
      const relativeX = (event.clientX - rect.left) / zoomLevel;
      const relativeY = (event.clientY - rect.top) / zoomLevel;
      
      // Convert back to client coordinates in the calendar's internal space
      const adjustedClientX = rect.left + relativeX;
      const adjustedClientY = rect.top + relativeY;
      
      // Override event properties
      try {
        Object.defineProperty(event, 'clientX', {
          writable: true,
          configurable: true,
          value: adjustedClientX,
        });
        Object.defineProperty(event, 'clientY', {
          writable: true,
          configurable: true,
          value: adjustedClientY,
        });
      } catch {
        // If we can't modify the event, the coordinates will be wrong
        // This is a limitation we'll have to work with
      }
    };

    // Intercept events in capture phase before react-big-calendar processes them
    calendarElement.addEventListener('mousemove', adjustCoordinates, { capture: true, passive: false });
    calendarElement.addEventListener('mouseup', adjustCoordinates, { capture: true, passive: false });

    return () => {
      calendarElement.removeEventListener('mousemove', adjustCoordinates, { capture: true });
      calendarElement.removeEventListener('mouseup', adjustCoordinates, { capture: true });
    };
  }, [zoomLevel]);

  // Calculate inverse zoom to cancel out root zoom for correct drag and drop coordinates
  const inverseZoom = zoomLevel === 0 ? 1 : 1 / zoomLevel;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="body1" color="text.secondary">
          {CALENDAR_MESSAGES.NO_DUE_DATES}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={calendarRef}
      sx={{
        display: 'flex',
        flex: 1,
        minHeight: 0,
        height: '100%',
        padding: 2,
        // Apply inverse zoom to cancel out root zoom for correct drag and drop coordinates
        zoom: inverseZoom,
      }}
    >
      <DragAndDropCalendar
        localizer={localizer}
        events={events}
        startAccessor={(ev: unknown) => (ev as { start: Date }).start}
        endAccessor={(ev: unknown) => (ev as { end: Date }).end}
        date={activeDate}
        view={activeView}
        onNavigate={handleNavigate}
        onView={handleViewChange}
        toolbar={!hideToolbar}
        showAllEvents
        onSelectEvent={(event: Event) => {
          const calendarEvent = event as CalendarEvent;
          handleSelectEvent(calendarEvent);
        }}
        onEventDrop={handleEventDrop}
        draggableAccessor={() => true}
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent,
        }}
        views={[...CALENDAR_VIEWS]}
        popup
        showMultiDayTimes={false}
      />
    </Box>
  );
};

export default ModelsCalendar;

